import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useDispatch, useSelector } from 'react-redux';
import { setToken } from '../redux/actions/userActions';
import ViewUser from '../pages/ViewUser';
import ViewReferrals from '../pages/ViewReferrals';
import ViewEmployers from '../pages/ViewEmployers';
import ViewCandidates from '../pages/ViewCandidates';
import ViewJobs from '../pages/ViewJobs';
import ViewGigs from '../pages/ViewGigs';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ViewPendingGigApplications from '../pages/ViewPendingGigApplications';
import { CardGiftcard, CreditCard, Dashboard, ExpandLess, ExpandMore, Label, Notifications } from '@mui/icons-material';
import { Ballot } from '@mui/icons-material';
import { PendingActions } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { isSuperUser, userDetails } from '../redux/selectors/userSelector'
import HeaderComponent from '../components/HeaderComponent';
import ViewJobRoles from '../pages/ViewJobRoles';
import versionName from '../helpers/versionName.json';
import appBarBgColor from '../helpers/appBarBgColor.json';
import ViewAllAudit from '../pages/ViewAllAudit';
import ViewNotifications from '../pages/ViewNotifications';
import ViewUserLabels from '../pages/ViewUserLabels';
import NotificationPopover from '../components/NotificationPopover';
import { Badge } from '@mui/material';
import { getAdminNotificationsBadgeCount, setActiveManager } from '../redux/actions/commonActions';
import JobApplicantsModal from '../models/JobApplicantsModal';
import { getJobApplicantsList, setJobApplicantsPageNumber } from '../redux/actions/jobActions';
import constants from '../helpers/constants.json';
import ViewSubscriptionPlans from '../pages/ViewSubscriptionPlans';
import { getSingleCandidateData } from '../redux/actions/candidateActions';
import CandidateProfileModal from '../models/CandidateProfile';
import ViewDashboard from '../pages/ViewDashboard';
import ViewManagerDashboard from '../pages/ViewManagerDashboard';

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1, 0.5),
  height: '20px',
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  })
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

export default function Sidenav() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [expandMenu, setExpandMenu] = useState(false);
  const [showRipple, setShowRipple] = useState(false);

  const [subPathId, setSubPathId] = useState(null);

  const [anchorElement, setAnchorElement] = useState(null);

  const notificationBadgeCountResponse = useSelector(state => state.common.adminNotificationsBadge);
  const notificationBadgeCount = notificationBadgeCountResponse?.data;

  const [jobPostId, setJobPostId] = useState(null);
  const [isJobApplicantsModalOpen, setIsJobApplicantsModalOpen] = useState(false);
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);

  const jobsApplicantsResponse = useSelector((state) => state.job.jobApplicants);
  const jobApplicants = jobsApplicantsResponse;

  const candidateById = useSelector((state) => state.candidate.candidateById);
  const candidateDataById = candidateById?.data;

  const user = useSelector(userDetails);
  const superUser = useSelector(isSuperUser);

  useEffect(() => {
    // Extract the pathname from the location object
    const { pathname } = location;

    // Handle navigation based on the pathname
    if (pathname.startsWith('/app/dashboard')) {
      setSelectedMenuItem('dashboard');
      let subPath = matchPath('/app/dashboard/:managerId', pathname);
      if (subPath) {
        setSubPathId(subPath.params.managerId);
      } else {
        setSubPathId(null);
        let aManager = {
          'name': user?.name,
          'adminUserId': user?.adminUserId
        }
        dispatch(setActiveManager(aManager));
      }
    } else if (pathname.startsWith('/app/users')) {
      setSelectedMenuItem('users');
    } else if (pathname.startsWith('/app/referrals')) {
      setSelectedMenuItem('referrals');
    } else if (pathname.startsWith('/app/activity-logs')) {
      setSelectedMenuItem('activityLogs');
    } else if (pathname.startsWith('/app/employers')) {
      setSelectedMenuItem('employers');
    } else if (pathname.startsWith('/app/candidates')) {
      setSelectedMenuItem('candidates');
    } else if (pathname.startsWith('/app/jobs')) {
      setSelectedMenuItem('jobs');
    } else if (pathname.startsWith('/app/gigs')) {
      setSelectedMenuItem('gigs');
    } else if (pathname.startsWith('/app/pending-gig-applications')) {
      setSelectedMenuItem('pendingGigApplications');
    } else if (pathname.startsWith('/app/job-roles')) {
      setSelectedMenuItem('jobRoles');
    } else if (pathname.startsWith('/app/notifications')) {
      setSelectedMenuItem('notifications');
    } else if (pathname.startsWith('/app/user-labels')) {
      setSelectedMenuItem('userLabels');
    } else if (superUser && pathname.startsWith('/app/subscription-plans')) {
      setSelectedMenuItem('subscriptionPlans');
    }
  }, [location]);

  useEffect(() => {
    const fetchNotificationBadgeCount = () => {
      let paramJson = {
        'adminUserId': user.adminUserId
      }
      dispatch(getAdminNotificationsBadgeCount(paramJson));
    }

    // fetch initial notification badge count
    fetchNotificationBadgeCount();
    // start interval to fetch it every 5 minutes
    const interval = setInterval(fetchNotificationBadgeCount, 300000);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    // show ripple animation if badge count changes and it is greater than 0
    if (notificationBadgeCount > 0) {
      setShowRipple(true);
    } else {
      if (showRipple) {
        setShowRipple(false);
      }
    }
  }, [notificationBadgeCount]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(setToken(null));
    handleProfileMenuClose();
  };

  const handleViewUserClick = () => {
    setSelectedMenuItem('users');
    navigate('/app/users');
    handleProfileMenuClose();
  };

  const handleDrawerListItemClick = (key, urlPath) => {
    setSelectedMenuItem(key);
    navigate(urlPath);
  }

  const profileMenuItems = [
    { label: 'Users', onClick: handleViewUserClick },
    { label: 'Logout', onClick: handleLogout },
  ];

  const getVersionName = useMemo(
    () => versionName[process.env.REACT_APP_ENV], []
  );

  const getAppBarBgColor = useMemo(
    () => appBarBgColor[process.env.REACT_APP_ENV], []
  );

  const handleNotificationButton = (event) => {
    setAnchorElement(event.currentTarget);
    setShowRipple(false);
  }

  const handleCloseNotificationModal = () => setAnchorElement(null);

  const onNotificationItemClick = (notItem) => {
    if (notItem.type == constants.adminNotificationType.newJobApplication) {
      setJobPostId(notItem.actionData.jobId);
      dispatch(setJobApplicantsPageNumber(0));
      dispatch(getJobApplicantsList(notItem.actionData.jobId, 0));
      setIsJobApplicantsModalOpen(true);
    }
  }

  const onViewProfile = (notItem) => {
    if (notItem.type == constants.adminNotificationType.newJobApplication) {
      dispatch(getSingleCandidateData(notItem.actionData.candidateId));
      setIsCandidateModalOpen(true)
    }
  }

  const handleCloseCandidateModal = () => {
    setIsCandidateModalOpen(false);
  }

  const handleCloseJobApplicantsModal = () => {
    setIsJobApplicantsModalOpen(false);
  }

  const drawerListItems = [
    { key: "dashboard", title: "Dashboard", path: "/app/dashboard", icon: <Dashboard /> },
    { key: "candidates", title: "Candidates", path: "/app/candidates", icon: <PersonIcon /> },
    { key: "jobs", title: "Jobs", path: "/app/jobs", icon: <WorkIcon /> },
    { key: "employers", title: "Employers", path: "/app/employers", icon: <BadgeIcon /> },
    { key: "userLabels", title: "Labels", path: "/app/user-labels", icon: <Label /> },
    { key: "userNotifications", title: "User Notifications", path: "/app/user-notifications", icon: <Notifications /> },
    { key: "jobRoles", title: "Job Roles", path: "/app/job-roles", icon: <Ballot /> },
    { key: "referrals", title: "Referrals", path: "/app/referrals", icon: <CardGiftcard /> },
    { key: "activityLogs", title: "Activity Logs [Beta]", path: "/app/activity-logs", icon: <PendingActions /> },
    ...(superUser ? [{ key: "subscriptionPlans", title: "Subscription Plans", path: "/app/subscription-plans", icon: <CreditCard /> }] : []),
    { key: "gigs", title: "Gigs", path: "/app/gigs", icon: <AttachMoneyIcon /> },
    { key: "pendingGigApplications", title: "Pending Gig Applications", path: "/app/pending-gig-applications", icon: <PersonSearchIcon /> }
  ]

  const renderDrawerListItem = (key, title, icon, path) => {
    return (
      <ListItem
        key={key}
        disablePadding
        onClick={() => handleDrawerListItemClick(key, path)}
        sx={{
          display: 'block',
          backgroundColor: selectedMenuItem === key ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Tooltip title={title} placement='right'>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              my: "2px"
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              {icon}
            </ListItemIcon>
            <ListItemText primary={title} sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  }

  const renderExpandMenuButton = () => {
    return (
      <ListItem
        key="show_more"
        disablePadding
        onClick={() => setExpandMenu(prevState => !prevState)}
        sx={{
          display: 'block',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Tooltip title={expandMenu ? "Show Less" : "Show More"} placement='right'>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              my: "2px"
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              {expandMenu ? <ExpandLess /> : <ExpandMore />}
            </ListItemIcon>
            <ListItemText primary="Show More" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </Tooltip>
      </ListItem>
    )
  }

  const userAccountDetail = user && user.mobileNumber + " - " + user.name;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <style>{`
          .MuiTableCell-head {
            padding: 10px 8px;
          }
          .MuiTableCell-body {
            padding: 10px 8px;
          }

        .ripple-badge {
          position: relative;
          z-index: 1;
        }

        .ripple-badge::after,
        .ripple-badge::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          background-color: rgba(255, 255, 255, 0.4);
          border-radius: 50%;
          transform: translate(-50%, -50%) scale(1);
          z-index: -1;
          animation: rippleEffect 1.2s ease-out infinite;
        }

        .ripple-badge::before {
          animation-delay: 0.6s; /* Halfway delay to stagger the second ripple */
        }

        @keyframes rippleEffect {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.7;
          }
          70% {
            transform: translate(-50%, -50%) scale(2.2);
            opacity: 0.3;
          }
          100% {
            transform: translate(-50%, -50%) scale(3);
            opacity: 0;
          }
        }
      `}</style>

      <AppBar position="fixed" open={open} sx={{ backgroundColor: getAppBarBgColor }}>
        <Toolbar variant='dense'>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={process.env.PUBLIC_URL + '/rozgaar_logo.png'}
              style={{ maxHeight: '3em', width: 'auto' }}
              alt=''
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={handleNotificationButton}
                style={{ marginRight: '4px' }}
              >
                <Badge badgeContent={notificationBadgeCount} color="error" className={showRipple ? 'ripple-badge' : ''}>
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title={userAccountDetail}>
              <IconButton
                color="inherit"
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              {profileMenuItems.map((item) => (
                <MenuItem key={item.label} onClick={item.onClick}>
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>

        <List>
          {drawerListItems.slice(0, expandMenu ? undefined : 4).map((item) => (
            renderDrawerListItem(item.key, item.title, item.icon, item.path)
          ))}
          {renderExpandMenuButton()}
        </List>

      </Drawer >

      <Box component="main" sx={{
        flexGrow: 1,
        p: 2,
        marginTop: '46px',
        ...(selectedMenuItem === 'dashboard' && { backgroundColor: '#f6f6f6' })
      }}>
        {selectedMenuItem === 'dashboard' && (superUser ? (subPathId ? <ViewManagerDashboard managerId={subPathId} /> : <ViewDashboard />) : <ViewManagerDashboard managerId={user.adminUserId} />)}
        {selectedMenuItem === 'users' && <ViewUser />}
        {selectedMenuItem === 'referrals' && <ViewReferrals />}
        {selectedMenuItem === 'activityLogs' && <ViewAllAudit />}
        {selectedMenuItem === 'employers' && <ViewEmployers />}
        {selectedMenuItem === 'candidates' && <ViewCandidates />}
        {selectedMenuItem === 'jobs' && <ViewJobs />}
        {selectedMenuItem === 'gigs' && <ViewGigs />}
        {selectedMenuItem === 'pendingGigApplications' && <ViewPendingGigApplications />}
        {selectedMenuItem === 'jobRoles' && <ViewJobRoles />}
        {selectedMenuItem === 'userNotifications' && <ViewNotifications />}
        {selectedMenuItem === 'userLabels' && <ViewUserLabels />}
        {selectedMenuItem === 'subscriptionPlans' && <ViewSubscriptionPlans />}
        <span style={{ fontSize: '14px' }}>[{getVersionName}]</span>
      </Box >


      <NotificationPopover
        anchorElement={anchorElement}
        onClose={handleCloseNotificationModal}
        onNotificationItemClick={onNotificationItemClick}
        onViewProfile={onViewProfile}
      />

      {isJobApplicantsModalOpen && jobApplicants !== null && (
        <JobApplicantsModal
          jobPostId={jobPostId}
          isOpen={isJobApplicantsModalOpen}
          onClose={handleCloseJobApplicantsModal}
          jobApplicants={jobApplicants}
        />
      )}

      {isCandidateModalOpen && candidateDataById !== null &&
        <CandidateProfileModal
          isOpen={isCandidateModalOpen}
          onClose={handleCloseCandidateModal}
          onLabelsAssigned={() => { }}
          candidateData={candidateDataById}
          shouldScrollToRemarks={false}
        />
      }

    </Box >
  );
}

